import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { PageHeader } from '@/components/feedback/PageHeader';
import { FillPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/lib/utils/date';
import type { ColumnDef } from '@tanstack/react-table';
import { AudioLines, Copy, Eye, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeleteTtsFlow, useTtsAccess, useTtsFlowList } from '../api';
import { TtsFlowPreviewModal } from '../components/TtsFlowPreviewModal';
import { REVIEW_STATUS, templateLabel, type TtsFlowListRow } from '../types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export function TtsFlowListPage() {
  const navigate = useNavigate();
  const query = useTtsFlowList();
  const ttsAccess = useTtsAccess();
  const del = useDeleteTtsFlow();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<TtsFlowListRow | null>(null);
  const [previewRow, setPreviewRow] = useState<TtsFlowListRow | null>(null);
  const columns = useMemo<ColumnDef<TtsFlowListRow>[]>(() => {
    return [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'ttsFlowName',
        header: 'Flow',
        cell: ({ row }) => (
          <Link
            to={`/view-tts-flow/${row.original.id}`}
            className="flex items-center gap-2 font-medium text-foreground hover:underline"
          >
            <AudioLines className="h-3.5 w-3.5 text-muted-foreground" />
            {row.original.ttsFlowName}
          </Link>
        ),
      },
      {
        accessorKey: 'templateId',
        header: 'Template',
        size: 160,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {templateLabel(row.original.templateId)}
          </Badge>
        ),
      },
      {
        accessorKey: 'campaignIds',
        header: 'Campaigns',
        size: 180,
        cell: ({ row }) => {
          const ids = (row.original.campaignIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

          if (ids.length === 0) {
            return (
              <span className="text-xs text-muted-foreground" title="No associated campaigns">
                —
              </span>
            );
          }

          const MAX_VISIBLE = 3;
          const visible = ids.slice(0, MAX_VISIBLE);
          const overflow = ids.length - visible.length;

          return (
            <div className="flex flex-wrap items-center gap-1">
              {visible.map((id, idx) => (
                <Badge
                  key={`${id}-${idx}`}
                  variant="outline"
                  className="font-mono text-[11px] font-normal tabular-nums"
                >
                  {id}
                </Badge>
              ))}

              {overflow > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 items-center gap-0.5 rounded-md border border-dashed border-border bg-secondary px-1.5 text-[11px] font-normal text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Show all ${ids.length} campaign IDs`}
                    >
                      +{overflow} more
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-56 p-3" sideOffset={6}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">
                        Campaigns ({ids.length})
                      </span>
                    </div>
                    <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto">
                      {ids.map((id, idx) => (
                        <Badge
                          key={`${id}-${idx}`}
                          variant="outline"
                          className="font-mono text-[11px] font-normal tabular-nums"
                        >
                          {id}
                        </Badge>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => {
          const status = String(row.original.status ?? '');
          const cfg = REVIEW_STATUS[status] ?? { label: 'Unknown', className: '' };
          return (
            <Badge variant="secondary" className={cfg.className}>
              {cfg.label}
            </Badge>
          );
        },
      },
      {
        id: 'preview',
        header: 'Flow Preview',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Preview ${row.original.ttsFlowName}`}
            onClick={() => setPreviewRow(row.original)}
          >
            <Eye className="h-4 w-4 text-muted-foreground" />
          </Button>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{row.original.ttsFlowName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  ttsAccess.reason === 'no-module'
                    ? toast.error(ttsAccess.message ?? 'TTS is not available.')
                    : navigate(`/update-tts-flow/${row.original.id}`)
                }
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  ttsAccess.blocked
                    ? toast.error(ttsAccess.message ?? 'TTS is not available.')
                    : navigate(`/clone-tts-flow/${row.original.id}`)
                }
              >
                <Copy className="h-4 w-4" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ];
  }, [navigate, ttsAccess.reason, ttsAccess.message]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.ttsFlowName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the TTS flow.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Flows"
        description="Text-to-speech automations — play prompts, gather DTMF, optionally patch to agents."
        actions={
          ttsAccess.reason === 'no-module' ? (
            <Button onClick={() => toast.error(ttsAccess.message ?? 'TTS is not available.')}>
              <Plus className="h-4 w-4" />
              New TTS flow
            </Button>
          ) : (
            <Button asChild>
              <Link to="/add-tts-flow">
                <Plus className="h-4 w-4" />
                New TTS flow
              </Link>
            </Button>
          )
        }
      />

      <DataTable<TtsFlowListRow, unknown>
        tableId="tts-flow:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search flows…"
        emptyTitle="No TTS flows yet"
        emptyDescription="Create a TTS flow to play synthesized speech to your callers."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="tts-flows.csv"
        pageSize={10}
      />

      {previewRow && (
        <TtsFlowPreviewModal
          key={previewRow.id}
          rowId={Number(previewRow.id)}
          flowName={previewRow.ttsFlowName}
          templateId={previewRow.templateId}
          createdAt={previewRow.createdAt}
          onClose={() => setPreviewRow(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete TTS flow?"
        description={
          <>
            TTS flow{' '}
            <span className="font-medium text-foreground">{pendingDelete?.ttsFlowName}</span> will
            be deleted. Campaigns referencing it will stop working.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
